import React, { useMemo, useState, useEffect } from "react";
import AddTaskModal from "../components/AddTaskModal";
import { Linkedin, Users, Sparkles, Clock } from 'lucide-react';
import api from '../api/api';
import { useLookups } from '../context/LookupContext';
import { useTheme } from '../hooks/useTheme';

export default function BrandManager() { 
  const { isDarkMode, setIsDarkMode } = useTheme();
  const [linkedinUrl, setLinkedinUrl] = useState('');

  return (
    <>
      <div className={`min-h-screen ${isDarkMode ? 'bg-surface text-slate-100' : 'bg-surface text-slate-900'}`}>
        {/* Manage Brand Section - Merged at the top */}
        <section className={`max-w-6xl mx-auto px-4 py-6 md:py-8 ${isDarkMode ? 'text-slate-100' : ''}`}>
          <header className="mb-6">
            <h1 className="text-2xl md:text-3xl font-bold mb-2">Manage My Brand</h1>
            <p className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>Optimize your professional presence across platforms using AI-driven insights.</p>
          </header>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8 mb-6 md:mb-8">
            {/* LinkedIn Optimization Card */}
            <div className={`rounded-2xl border shadow-sm p-5 md:p-8 relative overflow-hidden ${
              isDarkMode ? 'border-slate-700 bg-slate-800' : 'bg-white border-slate-200'
            }`}>
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-blue-50 rounded-xl text-blue-600">
                  <Linkedin size={28} />
                </div>
                <div>
                  <h2 className={`text-xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>LinkedIn Profile Audit</h2>
                  <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                    <Sparkles size={12} /> AI Powered
                  </span>
                </div>
              </div>

              <p className={`mb-6 leading-relaxed ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                Our AI "BizBot" will analyze your profile to provide recommendations on headlines,
                summaries, and skill gaps to increase your visibility to recruiters.
              </p>

              <div className="space-y-4">
                <label className={`block text-sm font-semibold ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Your LinkedIn URL</label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="text"
                    placeholder="https://linkedin.com/in/username"
                    value={linkedinUrl}
                    onChange={(e) => setLinkedinUrl(e.target.value)}
                    className={`flex-1 min-w-0 px-4 py-3 rounded-xl border outline-none transition-all ${
                      isDarkMode 
                        ? 'border-slate-600 bg-slate-700 text-slate-100 focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500' 
                        : 'border-slate-200 focus:ring-4 focus:ring-blue-50 focus:border-blue-500'
                    }`}
                  />
                  <button className="bg-blue-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-blue-700 transition-all">
                    Analyze
                  </button>
                </div>
              </div>
            </div>

            {/* Meetup Recommendations Card */}
            <div className={`rounded-2xl border shadow-sm p-5 md:p-8 relative grayscale opacity-75 ${
              isDarkMode ? 'border-slate-700 bg-slate-800' : 'bg-white border-slate-200'
            }`}>
              <div className="absolute top-4 right-4 flex items-center gap-1.5 px-3 py-1 bg-amber-50 text-amber-700 rounded-full text-xs font-bold border border-amber-100">
                <Clock size={14} /> Coming Soon
              </div>

              <div className="flex items-center gap-3 mb-6 mt-8 md:mt-0">
                <div className="p-3 bg-red-50 rounded-xl text-red-600">
                  <Users size={28} />
                </div>
                <div>
                  <h2 className={`text-xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Networking Matches</h2>
                </div>
              </div>

              <p className={`mb-6 leading-relaxed ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                Get personalized Meetup group and event suggestions based on your career
                intents and industry focus areas.
              </p>

              <div className={`mt-8 p-4 border border-dashed rounded-xl text-center ${
                isDarkMode ? 'bg-slate-700/50 border-slate-600' : 'bg-slate-50 border-slate-300'
              }`}>
                <p className={`text-sm font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  We're currently training the robot to find the best local events for you.
                </p>
              </div>
            </div>
          </div>

          {/* Integration Tip */}
          <div className={`border rounded-2xl p-6 flex items-start gap-4 ${
            isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-primary-50 border-primary-100'
          }`}>
            <div className={`p-2 rounded-lg text-primary-600 shadow-sm ${
              isDarkMode ? 'bg-slate-700' : 'bg-white'
            }`}>
              <Sparkles size={20} />
            </div>
            <div>
              <h4 className={`font-bold ${isDarkMode ? 'text-primary-300' : 'text-primary-900'}`}>Pro-Tip</h4>
              <p className={`text-sm ${isDarkMode ? 'text-primary-200' : 'text-primary-800'}`}>
                Keeping your brand consistent across LinkedIn and Meetup increases your "Networking Score" by up to 40%.
              </p>
            </div>
          </div>
        </section>
    </div>
    </>
  );
}